import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Plus,
  Edit,
  Trash2,
  Search,
  RefreshCw,
  Save,
  X,
  Download,
  Upload,
  Smartphone,
  FileText,
  Calendar,
  User,
  Eye,
  EyeOff,
  CheckCircle,
  AlertCircle,
  MoreVertical,
  Copy,
  ExternalLink,
} from "lucide-react";
import axios from "axios";
import { toast } from "react-toastify";
import { server } from "../server";
import { MdUpdate } from "react-icons/md";
import { appsList } from "../jsonsFiles/appsList";

const AppUpdateManager = () => {
  const [updates, setUpdates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [showDeleted, setShowDeleted] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingUpdate, setEditingUpdate] = useState(null);
  const [formData, setFormData] = useState({
    versionNo: "",
    appName: "",
    updateSummary: "",
  });

  // Fetch updates on component mount
  useEffect(() => {
    fetchUpdates();
  }, [showDeleted]);

  const fetchUpdates = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${server}/appUpdate/all`, {
        params: { deleted: showDeleted },
      });
      setUpdates(data.data);
    } catch (error) {
      toast.error("Failed to fetch app updates");
      console.error("Error fetching app updates:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddUpdate = async (e) => {
    e.preventDefault();
    if (
      !formData.versionNo.trim() ||
      !formData.updateSummary.trim() ||
      !formData.appName
    ) {
      toast.error("Please fill in all fields");
      return;
    }

    try {
      setSubmitting(true);
      const { data } = await axios.post(
        `${server}/appUpdate/new`,
        {
          versionNo: formData.versionNo.trim(),
          appName: formData.appName.trim(),
          updateSummary: formData.updateSummary.trim(),
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (data.success) {
        toast.success("App update added successfully!");
        setFormData({ versionNo: "", updateSummary: "" });
        setShowAddModal(false);
        fetchUpdates(); // Refresh the list
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to add app update");
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditUpdate = async (update) => {
    if (
      !formData.versionNo.trim() ||
      !formData.updateSummary.trim() ||
      !formData.appName
    ) {
      toast.error("Please fill in all fields");
      return;
    }

    try {
      setSubmitting(true);
      const { data } = await axios.put(
        `${server}/appUpdate/updateById`,
        {
          versionNo: formData.versionNo.trim(),
          appName: formData.appName.trim(),
          updateSummary: formData.updateSummary.trim(),
        },
        {
          params: {
            versionId: update.id,
          },
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (data.success) {
        toast.success("App update updated successfully!");
        setEditingUpdate(null);
        setFormData({ versionNo: "", updateSummary: "" });
        fetchUpdates(); // Refresh the list
      }
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to update app update"
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteUpdate = async (updateId) => {
    try {
      setSubmitting(true);
      const { data } = await axios.delete(`${server}/appUpdate/deleteById`, {
        params: { versionId: updateId },
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      if (data.success) {
        toast.success("App update deleted!");
        fetchUpdates(); // Refresh the list
      }
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to delete app update"
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteUpdatePermanently = async (updateId) => {
    try {
      setSubmitting(true);
      const { data } = await axios.delete(
        `${server}/appUpdate/deleteForeverById`,
        {
          params: { versionId: updateId },
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (data.success) {
        toast.success("App update deleted!");
        fetchUpdates(); // Refresh the list
      }
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to delete app update"
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleRestoreUpdate = async (updateId) => {
    try {
      setSubmitting(true);
      const { data } = await axios.put(
        `${server}/appUpdate/recoverDeleteById`,
        null,
        {
          params: { versionId: updateId },
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (data.success) {
        toast.success("App update restored successfully!");
        fetchUpdates(); // Refresh the list
      }
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to restore app update"
      );
    } finally {
      setSubmitting(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard!");
  };

  // Filter updates based on search and deleted status
  const filteredUpdates = updates.filter((update) => {
    const matchesSearch =
      update.versionNo.toString().includes(searchTerm) ||
      update.updateSummary.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesDeleted = showDeleted ? update.deleted : !update.deleted;
    return matchesSearch && matchesDeleted;
  });

  // Sort updates by version number (descending)
  const sortedUpdates = [...filteredUpdates].sort(
    (a, b) => b.versionNo - a.versionNo
  );

  // Get latest version
  const latestVersion =
    updates.length > 0
      ? Math.max(...updates.map((update) => update.versionNo))
      : 0;

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.6,
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut",
      },
    },
  };

  const modalVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.3,
        ease: "easeOut",
      },
    },
    exit: {
      opacity: 0,
      scale: 0.8,
      transition: {
        duration: 0.2,
      },
    },
  };

  const backdropVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const startEdit = (update) => {
    setEditingUpdate(update);
    setFormData({
      versionNo: update.versionNo.toString(),
      appName: update.appName || "",
      updateSummary: update.updateSummary,
    });
  };

  const cancelEdit = () => {
    setEditingUpdate(null);
    setFormData({ versionNo: "", appName: "", updateSummary: "" });
  };

  const startAdd = () => {
    setFormData({ versionNo: "", appName: "", updateSummary: "" });
    setShowAddModal(true);
  };

  return (
    <div className="min-h-screen bg-gray-900 py-8 px-4 sm:px-6 lg:px-8">
      <motion.div
        className="max-w-6xl mx-auto"
        initial="hidden"
        animate="visible"
        variants={containerVariants}
      >
        {/* Header */}
        <motion.div className="text-center mb-8" variants={itemVariants}>
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="p-3 bg-gradient-to-br from-green-600 to-blue-600 rounded-2xl shadow-lg">
              <Smartphone className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-4xl font-bold text-white">
              App Update Manager
            </h1>
          </div>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            Manage app version updates and release notes. Keep your users
            informed about new features and improvements.
          </p>
        </motion.div>

        {/* Stats and Controls */}
        <motion.div
          className="bg-gray-800 rounded-2xl p-6 mb-6 shadow-lg border border-gray-700"
          variants={itemVariants}
        >
          <div className="flex flex-col lg:flex-row gap-6 items-center justify-between">
            {/* Stats */}
            <div className="flex flex-wrap gap-4">
              <div className="flex items-center gap-3 p-3 bg-gray-700 rounded-xl">
                <MdUpdate className="w-5 h-5 text-blue-400" />
                <div>
                  <div className="text-2xl font-bold text-white">
                    {updates.length}
                  </div>
                  <div className="text-sm text-gray-400">Total Updates</div>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 bg-gray-700 rounded-xl">
                <Download className="w-5 h-5 text-green-400" />
                <div>
                  <div className="text-2xl font-bold text-white">
                    v{latestVersion}
                  </div>
                  <div className="text-sm text-gray-400">Latest Version</div>
                </div>
              </div>
            </div>

            {/* Search and Actions */}
            <div className="flex flex-col sm:flex-row gap-3 flex-1 max-w-2xl">
              {/* Search */}
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="text"
                    placeholder="Search by version or summary..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 bg-gray-700 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                  />
                </div>
              </div>

              {/* Filters and Actions */}
              <div className="flex gap-3">
                {/* Show Deleted Toggle */}
                <button
                  onClick={() => setShowDeleted(!showDeleted)}
                  className={`flex items-center gap-2 px-4 py-3 rounded-xl transition-all duration-200 ${
                    showDeleted
                      ? "bg-red-500/20 text-red-400 border border-red-500/30"
                      : "bg-gray-700 text-gray-300 border border-gray-600 hover:bg-gray-600"
                  }`}
                >
                  {showDeleted ? (
                    <EyeOff className="w-4 h-4" />
                  ) : (
                    <Eye className="w-4 h-4" />
                  )}
                  {showDeleted ? "Hide Deleted" : "Show Deleted"}
                </button>

                {/* Refresh */}
                <button
                  onClick={fetchUpdates}
                  disabled={loading}
                  className="flex items-center gap-2 px-4 py-3 bg-gray-700 text-gray-300 border border-gray-600 rounded-xl hover:bg-gray-600 transition-all duration-200 disabled:opacity-50"
                >
                  <RefreshCw
                    className={`w-4 h-4 ${loading ? "animate-spin" : ""}`}
                  />
                  Refresh
                </button>

                {/* Add Update */}
                <button
                  onClick={startAdd}
                  className="flex items-center gap-2 px-4 py-3 bg-gradient-to-br from-green-600 to-blue-600 text-white rounded-xl hover:shadow-lg hover:shadow-green-500/20 transition-all duration-200"
                >
                  <Plus className="w-4 h-4" />
                  Add Update
                </button>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Updates List */}
        <motion.div variants={itemVariants}>
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <RefreshCw className="w-8 h-8 text-blue-400 animate-spin" />
            </div>
          ) : sortedUpdates.length === 0 ? (
            <div className="text-center py-12 bg-gray-800 rounded-2xl border border-gray-700">
              <Smartphone className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-400 mb-2">
                {showDeleted
                  ? "No deleted updates found"
                  : "No app updates found"}
              </h3>
              <p className="text-gray-500">
                {searchTerm
                  ? "Try adjusting your search terms"
                  : showDeleted
                  ? "Deleted updates will appear here"
                  : "Get started by creating your first app update"}
              </p>
            </div>
          ) : (
            <div className="grid gap-4">
              {sortedUpdates.map((update) => (
                <motion.div
                  key={update.id}
                  layout
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  className={`bg-gray-800 rounded-2xl p-6 border transition-all duration-200 ${
                    update.deleted
                      ? "border-red-500/30 bg-red-500/5"
                      : update.versionNo === latestVersion
                      ? "border-green-500/30 bg-green-500/5"
                      : "border-gray-700 hover:border-gray-600"
                  }`}
                >
                  <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
                    {/* Update Info */}
                    <div className="flex-1">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className="flex items-center gap-2">
                            <MdUpdate className="w-5 h-5 text-blue-400" />
                            <h3 className="text-xl font-semibold text-white">
                              Version {update.versionNo}
                            </h3>
                            {update.appName && (
                              <span className="px-2 py-1 bg-blue-500/20 text-blue-400 rounded-full text-xs font-medium border border-blue-500/30">
                                {update.appName}
                              </span>
                            )}
                            {update.versionNo === latestVersion && (
                              <span className="px-2 py-1 bg-green-500/20 text-green-400 rounded-full text-xs font-medium">
                                Latest
                              </span>
                            )}
                          </div>
                          {update.deleted && (
                            <span className="px-2 py-1 bg-red-500/20 text-red-400 rounded-full text-xs font-medium">
                              Deleted
                            </span>
                          )}
                        </div>
                      </div>

                      {editingUpdate?.id === update.id ? (
                        <div className="space-y-3">
                          <div>
                            <label className="block text-sm font-medium text-gray-300 mb-1">
                              App Name
                            </label>
                            <select
                              value={formData.appName}
                              onChange={(e) =>
                                setFormData((prev) => ({
                                  ...prev,
                                  appName: e.target.value,
                                }))
                              }
                              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 mb-3"
                            >
                              <option value="" disabled>
                                Select App
                              </option>
                              <option value="Quiz App">Quiz App</option>
                              <option value="Academic App">Academic App</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-300 mb-1">
                              Version Number
                            </label>
                            <input
                              value={formData.versionNo}
                              onChange={(e) =>
                                setFormData((prev) => ({
                                  ...prev,
                                  versionNo: e.target.value,
                                }))
                              }
                              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              placeholder="Enter version number..."
                              min="0"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-300 mb-1">
                              Update Summary
                            </label>
                            <textarea
                              value={formData.updateSummary}
                              onChange={(e) =>
                                setFormData((prev) => ({
                                  ...prev,
                                  updateSummary: e.target.value,
                                }))
                              }
                              rows="3"
                              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                              placeholder="Enter update summary..."
                            />
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="mb-3">
                            <p className="text-gray-300 whitespace-pre-wrap">
                              {update.updateSummary}
                            </p>
                          </div>
                          <div className="flex flex-wrap items-center gap-4 text-sm text-gray-400">
                            <div className="flex items-center gap-1">
                              <Calendar className="w-4 h-4" />
                              <span>{formatDate(update.createdAt)}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <User className="w-4 h-4" />
                              <span>By User {update.createdBy}</span>
                            </div>
                          </div>
                        </>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {editingUpdate?.id === update.id ? (
                        <>
                          <button
                            onClick={() => handleEditUpdate(update)}
                            disabled={submitting}
                            className="flex items-center gap-2 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                          >
                            <Save className="w-4 h-4" />
                            Save
                          </button>
                          <button
                            onClick={cancelEdit}
                            disabled={submitting}
                            className="flex items-center gap-2 px-3 py-2 bg-gray-600 text-gray-300 rounded-lg hover:bg-gray-500 transition-colors disabled:opacity-50"
                          >
                            <X className="w-4 h-4" />
                            Cancel
                          </button>
                        </>
                      ) : update.deleted ? (
                        <>
                          <button
                            onClick={() => handleRestoreUpdate(update.id)}
                            disabled={submitting}
                            className="flex items-center gap-2 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                          >
                            <CheckCircle className="w-4 h-4" />
                            Restore
                          </button>
                          <button
                            onClick={() =>
                              handleDeleteUpdatePermanently(update.id)
                            }
                            disabled={submitting}
                            className="flex items-center gap-2 px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                          >
                            <Trash2 className="w-4 h-4" />
                            Delete Permanently
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            onClick={() =>
                              copyToClipboard(update.updateSummary)
                            }
                            className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                            title="Copy summary"
                          >
                            <Copy className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => startEdit(update)}
                            disabled={submitting}
                            className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                          >
                            <Edit className="w-4 h-4" />
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeleteUpdate(update.id)}
                            disabled={submitting}
                            className="flex items-center gap-2 px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                          >
                            <Trash2 className="w-4 h-4" />
                            Delete
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </motion.div>

        {/* Stats */}
        {!loading && updates.length > 0 && (
          <motion.div
            className="mt-6 text-center text-gray-400 text-sm"
            variants={itemVariants}
          >
            Showing {sortedUpdates.length} of {updates.length} app updates
            {showDeleted && " (including deleted)"}
          </motion.div>
        )}
      </motion.div>

      {/* Add Update Modal */}
      <AnimatePresence>
        {showAddModal && (
          <motion.div
            variants={backdropVariants}
            initial="hidden"
            animate="visible"
            exit="hidden"
            className="fixed inset-0 bg-slate-950 bg-opacity-90 rounded-3xl flex justify-center p-4 z-50"
            onClick={() => setShowAddModal(false)}
          >
            <motion.div
              variants={modalVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              className="bg-gray-800 rounded-2xl p-6 max-w-md w-full h-fit border border-gray-700"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-semibold text-white">
                  Add New App Update
                </h3>
                <button
                  onClick={() => setShowAddModal(false)}
                  className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-gray-400" />
                </button>
              </div>

              <form onSubmit={handleAddUpdate} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    App Name *
                  </label>
                  <select
                    value={formData.appName}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        appName: e.target.value,
                      }))
                    }
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                    required
                  >
                    <option value="" disabled>
                      Select App
                    </option>
                    {appsList?.map((e, i) => (
                      <option key={i} value={e?.name}>
                        {e?.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Version Number *
                  </label>
                  <input
                    value={formData.versionNo}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        versionNo: e.target.value,
                      }))
                    }
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                    placeholder="Enter version number (e.g., 1.0.0, 1.2.1, 2.1.1...)"
                    min="0"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Update Summary *
                  </label>
                  <textarea
                    value={formData.updateSummary}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        updateSummary: e.target.value,
                      }))
                    }
                    rows="4"
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 resize-none"
                    placeholder="Describe the updates, new features, bug fixes, etc..."
                    required
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowAddModal(false)}
                    className="flex-1 px-4 py-3 bg-gray-700 text-gray-300 rounded-xl hover:bg-gray-600 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-br from-green-600 to-blue-600 text-white rounded-xl hover:shadow-lg hover:shadow-green-500/20 transition-all duration-200 disabled:opacity-50"
                  >
                    {submitting ? (
                      <RefreshCw className="w-4 h-4 animate-spin" />
                    ) : (
                      <Plus className="w-4 h-4" />
                    )}
                    {submitting ? "Adding..." : "Add Update"}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AppUpdateManager;
